import { useState } from 'react';
import { useSearch } from 'wouter';
import { BUSINESS, SCHOOL_CLIENT_TYPES, quoteRequestSchema } from '@mopani/contracts';
import { Hero } from '../components/ui/primitives';
import { Field } from '../components/ui/Field';
import { ApiRequestError, submitQuoteRequest } from '../lib/api';
import { usePageMeta } from '../lib/seo';

/**
 * Request a Quote — the core revenue mechanism.
 *
 * In production this form rendered perfectly and captured NOTHING. It made zero
 * API calls (AUD3-004), every field had name=null (AUD3-015), and it displayed a
 * success panel unconditionally — telling buyers "we'll prepare a quotation"
 * when nothing had been sent (X-2). Zero enquiries were captured in ~2 months.
 *
 * This implementation:
 *   - submits to POST /api/quote and shows success ONLY on a real 201 (C-8)
 *   - names every field and links every label (R-6)
 *   - validates client-side against the SHARED contract schema, so the rules
 *     cannot drift from the backend's
 *   - surfaces server field errors returned by the API
 *   - sends the honeypot the backend already implements (A-11)
 *   - shows the real reference id the API returns (A-12)
 *
 * Structure preserved from the baseline (P-12): Institution Details → Contact
 * Details → What Do You Need? → "What Happens Next?" navy panel with 5 steps.
 */

/**
 * Which institution types are schools comes from the shared contract, so this
 * form and the API cannot disagree about who supplies an EMIS number.
 */
const SCHOOL_TYPES: readonly string[] = SCHOOL_CLIENT_TYPES;

const INSTITUTION_TYPES = [
  'Primary School',
  'Secondary School',
  'Government Clinic',
  'Government Office',
  'Community Organisation',
  'Other',
] as const;

const CATEGORIES = [
  'Cleaning Chemicals',
  'Paper Products',
  'Janitorial Supplies',
  'Hygiene Products',
  'Refuse Bags & PPE',
  'Delivery Support',
] as const;

const TIMING = ['As soon as possible', 'Next week', 'Start of next term', 'Other'] as const;

const NEXT_STEPS = [
  'We review your request and check stock availability.',
  'We prepare and email a formal quote within 24 hours.',
  'You confirm the quote and arrange payment (EFT).',
  'Procurement begins immediately after payment confirmation.',
  'Delivery and Proof of Delivery (POD) are finalised.',
] as const;

type FieldErrors = Record<string, string | undefined>;

export function Quote() {
  const search = useSearch();
  const prefillProduct = new URLSearchParams(search).get('product') ?? '';

  usePageMeta({
    title: 'Request a Quote',
    description:
      'Request a formal quotation from Mopani Suppliers. Tell us what your school, clinic or office needs and we will prepare a compliant quote within 24 hours.',
    path: '/quote',
  });

  const [institutionType, setInstitutionType] = useState('');
  const isSchool = SCHOOL_TYPES.includes(institutionType);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent'>('idle');
  const [reference, setReference] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    setFormError(null);

    const fd = new FormData(e.currentTarget);
    const categories = CATEGORIES.filter((c) => fd.get(`category-${slug(c)}`) === 'on');
    const requirements = String(fd.get('requirements') ?? '');
    const timing = String(fd.get('timing') ?? '');

    const input = {
      organisationName: String(fd.get('organisationName') ?? ''),
      contactName: String(fd.get('contactName') ?? ''),
      email: String(fd.get('email') ?? ''),
      phone: String(fd.get('phone') ?? ''),
      clientType: String(fd.get('clientType') ?? ''),
      // EMIS number for a school, company registration otherwise. This is the
      // stable identity that stops one institution being registered twice —
      // which, with affiliates, would mean one school claimed by two of them.
      registrationNumber: String(fd.get('registrationNumber') ?? ''),
      registrationType: SCHOOL_TYPES.includes(String(fd.get('clientType') ?? '')) ? 'EMIS' as const : 'BUSINESS' as const,
      area: String(fd.get('area') ?? ''),
      // Categories, role and timing are folded into the message so no submitted
      // information is lost — the live `quote_requests` table has no columns for
      // them and Rule 9 forbids silently changing the schema.
      message: [
        requirements,
        categories.length ? `Categories: ${categories.join(', ')}` : '',
        timing ? `Preferred timing: ${timing}` : '',
        fd.get('role') ? `Role: ${String(fd.get('role'))}` : '',
      ]
        .filter(Boolean)
        .join('\n\n'),
      items: categories.map((name) => ({ name })),
      website: String(fd.get('website') ?? ''),
    };

    const parsed = quoteRequestSchema.safeParse(input);
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      setErrors(Object.fromEntries(Object.entries(flat).map(([k, v]) => [k, v?.[0]])));
      setFormError('Please check the highlighted fields.');
      return;
    }

    setStatus('submitting');
    try {
      const res = await submitQuoteRequest(parsed.data);
      setReference(res.reference ?? null);
      setStatus('sent');
    } catch (err) {
      setStatus('idle');
      if (err instanceof ApiRequestError) {
        if (err.fieldErrors) {
          setErrors(Object.fromEntries(Object.entries(err.fieldErrors).map(([k, v]) => [k, v[0]])));
        }
        setFormError(
          err.status === 429
            ? 'You have sent several requests recently. Please wait a few minutes, or contact us directly.'
            : `${err.message} You can also reach us on ${BUSINESS.phoneDisplay}.`,
        );
      } else {
        setFormError(`Something went wrong. Please call or WhatsApp us on ${BUSINESS.phoneDisplay}.`);
      }
    }
  }

  return (
    <>
      <Hero
        title="Request a Quote"
        subtitle="Fill in the details below. We'll prepare a formal quotation tailored to your institution's specific requirements."
        size="sm"
        padding="pt-16 pb-12"
      />

      <section className="bg-white px-4 pb-20 pt-12">
        <div className="container-content grid gap-10 lg:grid-cols-[1.4fr_1fr]">
          <div className="card p-8">
            <h2 className="font-heading text-xl font-bold text-navy">Quote Details</h2>

            {status === 'sent' ? (
              /* C-8: shown ONLY after a real 201 from the API. */
              <div
                className="mt-8 rounded-lg border border-green-cta/40 bg-green-cta/5 p-8 text-center"
                role="status"
              >
                <h3 className="font-heading text-2xl font-bold text-navy">
                  Quote request received
                </h3>
                <p className="mx-auto mt-4 max-w-lg text-charcoal/80">
                  Thank you. We will review your requirements and prepare a formal quotation
                  shortly. We will contact you by email or WhatsApp if we need any clarification.
                </p>
                {reference && (
                  <p className="mt-6 text-sm text-charcoal/70">
                    Your reference:{' '}
                    <code className="rounded bg-muted px-2 py-1 font-mono text-charcoal">
                      {reference.slice(0, 8).toUpperCase()}
                    </code>
                  </p>
                )}
                <p className="mt-6 text-sm text-charcoal/70">
                  Need it sooner?{' '}
                  <a
                    href={BUSINESS.whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-green-accent hover:underline"
                  >
                    WhatsApp us on {BUSINESS.phoneDisplay}
                  </a>
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate className="mt-6 space-y-8">
                {formError && (
                  <div
                    className="field-error rounded border border-[#c0392b]/30 bg-[#c0392b]/5 p-3"
                    role="alert"
                  >
                    {formError}
                  </div>
                )}

                <fieldset className="space-y-5">
                  <legend className="font-heading text-base font-semibold text-charcoal">
                    Institution Details
                  </legend>
                  <Field
                    name="organisationName"
                    label="Institution Name"
                    required
                    error={errors.organisationName}
                  >
                    {(p) => (
                      <input type="text" placeholder="e.g. Phalaborwa High School" autoComplete="organization" {...p} />
                    )}
                  </Field>
                  <Field name="clientType" label="Institution Type" error={errors.clientType}>
                    {(p) => (
                      <select
                        {...p}
                        defaultValue=""
                        onChange={(e) => setInstitutionType(e.target.value)}
                      >
                        <option value="">Select type…</option>
                        {INSTITUTION_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                  <Field
                    name="registrationNumber"
                    label={isSchool ? 'EMIS Number' : 'Business Registration Number'}
                    required
                    error={errors.registrationNumber}
                  >
                    {(p) => (
                      <input
                        type="text"
                        placeholder={isSchool ? 'e.g. 920312345' : 'e.g. 2026/371021/07'}
                        {...p}
                      />
                    )}
                  </Field>
                  <p className="-mt-2 text-sm text-charcoal/60">
                    {isSchool
                      ? 'Your EMIS number, issued by the Department of Basic Education. It identifies your school uniquely so your account is never duplicated.'
                      : 'Your CIPC company registration number. It identifies your organisation uniquely so your account is never duplicated.'}
                  </p>
                </fieldset>

                <fieldset className="space-y-5">
                  <legend className="font-heading text-base font-semibold text-charcoal">
                    Contact Details
                  </legend>
                  <Field name="contactName" label="Contact Person" required error={errors.contactName}>
                    {(p) => <input type="text" placeholder="Full Name" autoComplete="name" {...p} />}
                  </Field>
                  <Field name="role" label="Role / Title">
                    {(p) => <input type="text" placeholder="e.g. Bursar, Facility Manager" {...p} />}
                  </Field>
                  <Field name="email" label="Email Address" error={errors.email}>
                    {(p) => <input type="email" placeholder="name@school.co.za" autoComplete="email" {...p} />}
                  </Field>
                  <Field
                    name="phone"
                    label="Phone / WhatsApp"
                    required
                    error={errors.phone}
                    hint="We may contact you on WhatsApp to confirm details."
                  >
                    {(p) => <input type="tel" placeholder="063 996 0861" autoComplete="tel" {...p} />}
                  </Field>
                  <Field name="area" label="Location / Area" error={errors.area}>
                    {(p) => <input type="text" placeholder="e.g. Namakgale" {...p} />}
                  </Field>
                </fieldset>

                <fieldset className="space-y-5">
                  <legend className="font-heading text-base font-semibold text-charcoal">
                    What Do You Need?
                  </legend>

                  <div>
                    <span className="field-label">Product Categories Needed</span>
                    <div className="mt-2 grid gap-3 sm:grid-cols-2">
                      {CATEGORIES.map((c) => (
                        <label key={c} className="flex items-center gap-3 text-sm text-charcoal">
                          <input
                            type="checkbox"
                            name={`category-${slug(c)}`}
                            className="h-4 w-4 rounded border-[#d7dde2] accent-[#75b843]"
                          />
                          {c}
                        </label>
                      ))}
                    </div>
                  </div>

                  <Field
                    name="requirements"
                    label="Specific Products & Quantities"
                    required
                    error={errors.message}
                  >
                    {(p) => (
                      <textarea
                        rows={5}
                        defaultValue={prefillProduct ? `${prefillProduct} — quantity: ` : ''}
                        placeholder="Please list what you need. E.g. 5x 48-pack Toilet Paper, 2x 5L Pine Disinfectant…"
                        {...p}
                      />
                    )}
                  </Field>

                  <Field name="timing" label="Preferred Delivery Timing">
                    {(p) => (
                      <select {...p} defaultValue="">
                        <option value="">Select…</option>
                        {TIMING.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                </fieldset>

                {/* Honeypot (A-11) */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="field-website">Leave this field empty</label>
                  <input id="field-website" name="website" type="text" tabIndex={-1} autoComplete="off" />
                </div>

                <div>
                  <button
                    type="submit"
                    className="btn btn-primary btn-hero w-full"
                    disabled={status === 'submitting'}
                  >
                    {status === 'submitting' ? 'Sending…' : 'Submit Request'}
                  </button>
                  <p className="mt-3 text-center text-sm text-charcoal/60">
                    Your details are used only to prepare and follow up on your quote request.
                  </p>
                </div>
              </form>
            )}
          </div>

          {/* "What Happens Next?" navy panel — preserved (P-12). */}
          <aside className="h-fit rounded-lg bg-navy p-8 text-white">
            <h2 className="font-heading text-2xl font-bold text-white">What Happens Next?</h2>
            <ol className="mt-6 space-y-5">
              {NEXT_STEPS.map((s, i) => (
                <li key={s} className="flex gap-4">
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-cta font-heading text-sm font-bold text-white"
                    aria-hidden="true"
                  >
                    {i + 1}
                  </span>
                  <p className="text-sm text-white/85">{s}</p>
                </li>
              ))}
            </ol>
            <div className="mt-8 border-t border-white/15 pt-6">
              <p className="text-sm font-medium italic text-white/85">
                Need immediate assistance?
                <br />
                {/* C-2: a real wa.me link. Production said "WhatsApp us on…" over a
                    tel: href pointing at the wrong number. */}
                <a
                  href={BUSINESS.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-green-cta hover:underline"
                >
                  WhatsApp us on {BUSINESS.phoneDisplay}
                </a>
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}
